import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileToBase64, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Camera, FilePlus2, RefreshCw } from "lucide-react";

/**
 * CreditNoteDialog — toggle form to create a credit note tied to a supplier and
 * (optionally) a specific invoice. The credit amount is deducted from the
 * target invoice's `payable_balance`.
 */
export default function CreditNoteDialog({ open, onClose, shift, onCreated }) {
  const [supplier, setSupplier] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [imgB64, setImgB64] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !shift) return;
    api
      .get("/invoices", { params: { location: shift.location } })
      .then((r) => setInvoices(r.data || []))
      .catch(() => setInvoices([]));
    setSupplier(""); setAmount(""); setNote(""); setImgB64(""); setInvoiceId("");
  }, [open, shift]);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImgB64(await fileToBase64(f));
  };

  const submit = async () => {
    if (!shift) return;
    if (!amount || Number(amount) <= 0) {
      toast.error("Amount must be > 0");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/credit-notes", {
        shift_id: shift.id,
        location: shift.location,
        supplier_name: supplier.trim(),
        invoice_id: invoiceId || null,
        amount: Number(amount),
        note: note.trim(),
        image_base64: imgB64 || null,
      });
      toast.success(`Credit note €${data.amount.toFixed(2)} saved`);
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save credit note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Add Credit Note / Returns · Πιστωτικό Σημείωμα
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Supplier · Προμηθευτής
            </Label>
            <Input
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="e.g. Pandora Bakeries"
              data-testid="credit-supplier"
              className="mt-1.5 min-h-[44px]"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Linked Invoice (optional) · Σύνδεση με Τιμολόγιο
            </Label>
            <Select value={invoiceId || "_none"} onValueChange={(v) => setInvoiceId(v === "_none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="credit-invoice-select">
                <SelectValue placeholder="Auto-link to latest invoice from supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Auto-link to latest invoice</SelectItem>
                {invoices.map((inv) => (
                  <SelectItem key={inv.id} value={inv.id}>
                    {inv.supplier_name || "?"} · {inv.invoice_number || "?"} · €{inv.total_amount.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Credit Amount (€) · Αξία Πίστωσης
            </Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="credit-amount"
              className="mt-1.5 min-h-[56px] text-2xl font-mono font-bold text-center border-2"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Note · Σημείωση
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. 6 expired yoghurts returned to driver"
              data-testid="credit-note"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Signed Credit Note Photo
            </Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="flex-1 min-h-[56px] flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-slate-500 text-slate-600">
                <Camera className="h-5 w-5" />
                <span className="font-semibold">{imgB64 ? "Change Photo" : "Capture Photo"}</span>
                <input type="file" accept="image/*" capture="environment" onChange={onPickImage} className="hidden" data-testid="credit-photo-input" />
              </label>
              {imgB64 && (
                <img src={`data:image/jpeg;base64,${imgB64}`} alt="" className="h-14 w-14 rounded-md border object-cover" data-testid="credit-photo-preview" />
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="credit-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="credit-submit-btn" className="bg-slate-900 hover:bg-slate-800 gap-2">
            <FilePlus2 className="h-4 w-4" />
            {busy ? "Saving…" : "Save Credit Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
